import { useParams } from '@tanstack/react-router';
import PageContent from '@zeus/core/layout/PageContent';
import Loader from '@zeus/core/Loader';
import { useFlowContext } from '_/contexts/FlowContext';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProject } from '_/contexts/ProjectContext';
import { useFlowSettings } from '_/hooks/useFlows';
import { Card } from 'antd';
import React, { ReactElement, useEffect, useCallback } from 'react';

import FlowSettingsItem from './FlowSettingsItem';

function FlowSettingsPage(): ReactElement {
  const params = useParams({ strict: false }) as Partial<{ orgSlug: string; projectSlug: string }>;
  const orgSlug = params.orgSlug || '';
  const projectSlug = params.projectSlug || '';
  const { projectId, flowSlug, flowId, selectedFlow } = useFlowContext();
  const { selectedOrganization } = useOrganization();
  const { selectedProject } = useProject();
  const { setBreadcrumbs, setOnRefresh, setRefreshLoading } = useTopHeader();
  const { data: items = [], isLoading, refetch, isRefetching } = useFlowSettings(projectId, flowSlug, flowId);

  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  // Set up breadcrumbs
  useEffect(() => {
    setBreadcrumbs([
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrganization?.name || '', href: `/orgs/${orgSlug}` },
      { title: 'Projects', href: `/orgs/${orgSlug}/projects` },
      { title: selectedProject?.name || '', href: `/orgs/${orgSlug}/projects/${projectSlug}/home` },
      { title: 'Flows', href: `/orgs/${orgSlug}/projects/${projectSlug}/flows` },
      {
        title: selectedFlow?.name || 'Flow',
        href: `/orgs/${orgSlug}/projects/${projectSlug}/flows/${flowSlug}`,
      },
      { title: 'Settings' },
    ]);

    return () => {
      setBreadcrumbs([]);
    };
  }, [setBreadcrumbs, selectedOrganization?.name, orgSlug, selectedProject?.name, projectSlug, selectedFlow?.name, flowSlug]);

  // Set up refresh functionality
  useEffect(() => {
    setOnRefresh(handleRefresh);
    setRefreshLoading(isRefetching);

    return () => {
      setOnRefresh(undefined);
      setRefreshLoading(false);
    };
  }, [setOnRefresh, setRefreshLoading, handleRefresh, isRefetching]);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <PageContent>
      {items.map((item) => (
        <Card key={item.id} style={{ marginBottom: '8px' }}>
          <FlowSettingsItem {...item} />
        </Card>
      ))}
    </PageContent>
  );
}

export default FlowSettingsPage;
